import React, {FunctionComponent, ReactNode, useCallback, useMemo, useState} from "react";
import {Box, Button, CircularProgress, IconButton, Theme, Tooltip, Typography, useMediaQuery} from "@mui/material";
import {AddOutlined, DeleteOutlined, EditOutlined, OpenInNewRounded} from "@mui/icons-material";
import {Link, useHistory} from "react-router-dom";
import {useUpdateEffect} from "react-use";
import PurchasePlanningInformationUpdateNameDialog, {PurchasePlanningInformationUpdateNameDialogProps} from "../planning-details/update-name-dialog";
import BizProductionApiService, {ApiServiceModels} from "../../../../../../core/services/api_service/production_api_service";
import DataCards, {DataCardsDataType, DataCardsProperty, DataCardsTopAction, DataCardsTopActionsFunc} from "../../../../../components/app-specific/data-cards";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import {useAppDispatch} from "../../../../../redux";
import PartnerDataGrid, {DataGridColumn, DataGridColumnAlignments, DataGridColumnPinnedTypes, DataGridColumnTypes, useDataGridSavedState} from "../../../../../containers/data-grid";
import useSearch, {SearchDataSettings, UseSearchFunction, UseSearchFunctionArg} from "../../../../../hooks/use-search";
import {ApiResponse} from "../../../../../../@bizkeytech/base/core";
import {DefaultPaginationInfo} from "../../../../../../core/constants/enums";
import useReportingSearchUtils, {UseReportingSearchUtilsArg} from "../../../../../hooks/use-reporting-search-utils";
import {routeFunctions} from "../../../../../../core/models/static/routes/app-routes";
import useInfiniteScrollSearchUtils from "../../../../../hooks/use-infinite-scroll-search-utils";
import QueryBuilder from "../../../../../../packages/query-builder";
import {setReduxDialogState} from "../../../../../redux/slices/dialogs/actions";
import useAccessLevels from "../../../../../hooks/use-access-levels";
import ReduxStoreRoutes from "../../../../../../core/models/static/routes/redux-store-routes";

const columnNames = {
    remove: 'remove',
    navigate: 'navigate',
    createdOn: 'createDateTime',
    createdBy: 'operatorName',
    buildDate: 'updateDateTime',
    name: 'title',
}

export interface DataEntry extends ApiServiceModels.ListPlanningsResponseDs {
}

export interface CardsDataEntry extends DataEntry, DataCardsDataType {
}


const initialUpdateNameDialogState: PurchasePlanningInformationUpdateNameDialogProps & { sessionId: DataEntry['id'] } = {
    open: false,
    close: () => void 0,
    name: '',
    update: async () => true,
    sessionId: ''
};

const PurchasePlanningListView: FunctionComponent = () => {
    const history = useHistory();
    const [data, setData] = useState<Array<DataEntry>>([]);
    const [updateNameDialogState, setUpdateNameDialogState] = useState(initialUpdateNameDialogState);
    const [updatingName, setUpdatingName] = useState<DataEntry['id']>();
    const [removing, setRemoving] = useState<DataEntry['id'][]>([]);
    const cardsData = useMemo<Array<CardsDataEntry>>(() => data.map((e) => ({...e, key: e.id})), [data]);
    const isMounted = useIsMounted();
    const dispatch = useAppDispatch();
    const operatorAccessLevels = useAccessLevels();
    const canManagePlanning = operatorAccessLevels.packages.purchasing.managePurchasePlanning;
    const canExportPlanning = operatorAccessLevels.packages.purchasing.exportPurchasePlanning;

    const isMobile = useMediaQuery<Theme>(theme => theme.breakpoints.down('sm'));

    const dataGridSaveConfig = useMemo(() => ({name: 'P_PlanningSessionsView_DG', version: 1}), [])

    /** Stable id so QueryBuilder/DataGrid identity survives remounts. */
    const dataGridId = 'PurchasePlanningListDG';
    const staticDataGridColumns = useMemo<Array<DataGridColumn>>(() => [
        {
            name: columnNames.navigate,
            type: DataGridColumnTypes.element,
            alignment: DataGridColumnAlignments.center,
            sortable: false,
            pinned: true,
            visibilityToggleable: false,
            resizable: false,
            width: 0,
        },
        {
            name: columnNames.remove,
            type: DataGridColumnTypes.element,
            alignment: DataGridColumnAlignments.center,
            sortable: false,
            pinned: true,
            pinnedType: DataGridColumnPinnedTypes.right,
            visibilityToggleable: false,
            resizable: false,
            width: 0,
        },
        {
            title: "Name",
            name: columnNames.name,
            type: DataGridColumnTypes.element,
            alignment: DataGridColumnAlignments.left,
            sortable: false,
            pinned: true,
            width: {minWidth: 150},
        },
        {
            title: "Created On",
            name: columnNames.createdOn,
            type: DataGridColumnTypes.dateTime,
            alignment: DataGridColumnAlignments.left,
            sortable: false,
            width: {minWidth: 140},
        },
        {
            title: "Created By",
            name: columnNames.createdBy,
            type: DataGridColumnTypes.string,
            alignment: DataGridColumnAlignments.left,
            sortable: false,
        },
        {
            title: "Calculated Date",
            name: columnNames.buildDate,
            type: DataGridColumnTypes.dateTime,
            alignment: DataGridColumnAlignments.left,
            sortable: false,
            width: {minWidth: 140},
        },
    ].filter((column) => canManagePlanning || column.name !== columnNames.remove) as Array<DataGridColumn>, [canManagePlanning]);

    const [savedDataGridColumns, dataGridSortBy, dataGridPageSize, dataGridDensity] = useDataGridSavedState({
        columns: staticDataGridColumns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    /**
     * Searches for a list of data for exporting or for showing the list of items in the view
     *
     * * Calls the api regarding this action and updated the pagination info and sets the [reports] value in the inner state if not for
     * exporting Otherwise, returns the crud response.
     */
    const fetchEntries = useCallback<(args: UseSearchFunctionArg, forExport?: boolean) => ApiResponse<DataEntry[]>>(async (args, forExport = false) => {
        const response = await BizProductionApiService.v1.planning.listPlannings({keyword: args.filters?.keyword, planningTypeId: 2}, args.cancelTokenSource?.token)
        if (!isMounted())
            return;
        if (response?.aborted)
            return;
        if (response?.resultFlag) {
            const newEntries = response.data ?? [];
            if (!forExport) {
                if (isMobile) {
                    setData(prevState => [...prevState, ...newEntries]);
                } else {
                    setData(newEntries);
                }
                args.setPaginationInfo(prevState => ({
                    ...prevState!,
                    length: response?.data?.length ?? 0,
                }))
            } else {
                response.data = newEntries;
                return response;
            }
        }
    }, [isMounted, isMobile])

    /**
     * Searches among the entries based on the filters from the user.
     */
    const search = useCallback<UseSearchFunction>(async (result) => {
        await fetchEntries(result, false);
    }, [fetchEntries]);

    const initialPaginationInfo = useMemo(() => dataGridPageSize ? {
        ...DefaultPaginationInfo,
        pageSize: dataGridPageSize
    } : DefaultPaginationInfo, [dataGridPageSize])

    const initialSearchDataSettings = useMemo<SearchDataSettings>(() => ({
        paginationInfo: initialPaginationInfo,
        orderBy: dataGridSortBy as any,
    }), [initialPaginationInfo, dataGridSortBy]);

    const searchData = useSearch(search, initialSearchDataSettings)
    const {setPaginationInfo} = searchData;

    /**
     * Updates the name of the planning session.
     */
    const updateName = useCallback(async (sessionId: string, name: string) => {
        setUpdatingName(sessionId);
        const response = await BizProductionApiService.v1.planning.updatePlanningProperties({
            id: sessionId,
            title: name,
        });
        if (!isMounted())
            return true;
        setUpdatingName(undefined);
        if (response?.resultFlag) {
            setData(prevState => prevState.map(entry => entry.id === sessionId ? {...entry, title: name} : entry));
        }
        return response?.resultFlag ?? false;
    }, [isMounted])

    /**
     * Opens the dialog for updating the name of the planning session.
     */
    const openUpdateNameDialog = useCallback((session: DataEntry) => {
        setUpdateNameDialogState({
            open: true,
            close: () => setUpdateNameDialogState(initialUpdateNameDialogState),
            name: session?.title ?? '',
            sessionId: session.id,
            update: (name) => updateName(session.id, name),
        });
    }, [updateName]);

    /**
     * Removes the planning session from the list.
     */
    const removePlanningSession = useCallback(async (sessionId: DataEntry['id']) => {
        setRemoving(prevState => [...prevState, sessionId]);
        const response = await BizProductionApiService.v1.planning.deletePlanning({id: sessionId});
        if (!isMounted())
            return;
        setRemoving(prevState => prevState.filter(id => id !== sessionId));
        if (response?.resultFlag) {
            setData(prevState => prevState.filter(entry => entry.id !== sessionId));
            setPaginationInfo?.(prevState => ({
                ...prevState!,
                length: prevState!.length - 1,
            }));
        }
        return response?.resultFlag ?? false;
    }, [isMounted, setPaginationInfo]);

    /**
     * Gets the confirmation for removing the planning session.
     */
    const getRemoveConfirmation = useCallback(async (session: DataEntry) => {
        const confirmed = await new Promise((resolve) => {
            dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.confirmation, {
                open: true,
                title: 'Confirm Removal',
                description: `Are you sure you want to remove planning: "${session.title}"? This action cannot be undone.`,
                cancelButtonText: "Cancel",
                confirmButtonText: "Continue",
                maxWidth: false,
                fullWidth: false,
                onClose: (confirmed) => {
                    resolve(confirmed);
                    return Promise.resolve(true);
                }
            }));
        })
        if (confirmed) {
            void removePlanningSession(session.id);
        }
    }, [dispatch, removePlanningSession]);

    /**
     * Formats the exported cell based on the column type.
     */
    const getDataGridCell = useCallback<Required<UseReportingSearchUtilsArg<DataEntry>>['getDataGridCell']>((row, column, continueMarker) => {
        switch (column.name) {
            case columnNames.remove:
                return (
                    <Tooltip title={removing.includes(row.id) ? 'Removing...' : 'Remove'}>
                        <Box component={'span'}>
                            <IconButton
                                color={'paper.error'}
                                onClick={() => getRemoveConfirmation(row)}
                                disabled={removing.includes(row.id)}
                            >
                                {removing.includes(row.id) ? <CircularProgress size={20}/> : <DeleteOutlined fontSize={'small'}/>}
                            </IconButton>
                        </Box>
                    </Tooltip>
                )
            case columnNames.navigate:
                return (
                    <Tooltip title={'View Information'}>
                        <Box component={'span'}>
                            <IconButton
                                color={'paper.primary'}
                                component={Link as any}
                                to={routeFunctions.purchase.planningDetails(row.id.toString())}
                            >
                                <OpenInNewRounded/>
                            </IconButton>
                        </Box>
                    </Tooltip>
                )
            case columnNames.name:
                if (!row.title) {
                    return continueMarker;
                }
                return (
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                        {
                            canManagePlanning &&
                            <IconButton
                                color={'paper.primary'}
                                onClick={() => openUpdateNameDialog(row)}
                                disabled={updatingName !== undefined}
                            >
                                {updatingName === row.id ? <CircularProgress size={20}/> : <EditOutlined fontSize={'small'}/>}
                            </IconButton>
                        }
                        <Typography component={'span'} variant={'body2'} fontWeight={600}>
                            {row.title}
                        </Typography>
                    </Box>
                );
            default:
                return continueMarker
        }
    }, [canManagePlanning, getRemoveConfirmation, openUpdateNameDialog, removing, updatingName])

    const {
        getExportInfo,
        dataGridRows,
    } = useReportingSearchUtils<DataEntry>({
        savedDataGridColumns,
        fetchEntries,
        searchData,
        getDataGridCell,
        data,
    });

    /**
     * With each change in [isMobile]:
     * - Updates the pagination info based on the initial search data settings
     */
    useUpdateEffect(() => {
        setPaginationInfo?.(initialSearchDataSettings.paginationInfo);
        setData([]);
    }, [isMobile, setPaginationInfo])

    const {loadMoreData, hasMoreToLoad} = useInfiniteScrollSearchUtils({searchData, dataLength: data.length});

    /**
     * Returns the title of the card.
     */
    const getCardTitle = useCallback<(data: CardsDataEntry) => ReactNode>((data) => {
        return (
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                {
                    canManagePlanning &&
                    <IconButton
                        color={'paper.primary'}
                        onClick={() => openUpdateNameDialog(data)}
                        disabled={updatingName !== undefined}
                    >
                        {updatingName === data.id ? <CircularProgress size={20}/> : <OpenInNewRounded/>}
                    </IconButton>
                }
                <Typography component={'span'} variant={'body2'} fontWeight={600}>
                    {data.title ?? '--'}
                </Typography>
            </Box>
        );
    }, [canManagePlanning, openUpdateNameDialog, updatingName])

    /**
     * Returns the top actions of the card.
     */
    const getCardTopActions = useCallback<DataCardsTopActionsFunc<CardsDataEntry>>((data) => {
        return [
            {
                key: 'navigate',
                color: 'paper.primary',
                component: Link as any,
                to: routeFunctions.purchase.planningDetails(data.id.toString()),
                children: <OpenInNewRounded sx={{padding: 0.25}}/>,
            },
            ...(canManagePlanning ? [{
                key: 'remove',
                color: 'paper.error',
                component: 'button',
                onClick: () => getRemoveConfirmation(data),
                children: removing.includes(data.id) ? <CircularProgress size={20}/> : <DeleteOutlined sx={{padding: 0.25}}/>,
            }] : []) as DataCardsTopAction[],
        ]
    }, [canManagePlanning, getRemoveConfirmation, removing])

    const cardProperties = useMemo<DataCardsProperty<CardsDataEntry>[]>(() => {
        return savedDataGridColumns
            .filter(column => ![
                columnNames.navigate,
                columnNames.remove,
                columnNames.name,
            ].includes(column.name!))
            .map(column => (
                {
                    key: column.name!,
                    propertyName: column.name as keyof CardsDataEntry,
                    title: column.title ?? column.name,
                    value: (propertyValue) => {
                        switch (column.name as keyof DataEntry) {
                            default:
                                return propertyValue;
                        }
                    },
                }
            ))
    }, [savedDataGridColumns])

    /**
     * Navigates to the planning session information page by creating a new temporary planning session.
     */
    const createPlanningSession = useCallback(async () => {
        history.push(routeFunctions.purchase.planningDetails('new'));
    }, [history])

    return (
        <>
            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 2, flexWrap: 'wrap'}}>
                <Typography variant={'subtitle1'} fontWeight={600}>
                    Purchase Planning list
                </Typography>
                {
                    canManagePlanning &&
                    <Button
                        variant={'outlined'}
                        color={'primary'}
                        startIcon={<AddOutlined/>}
                        onClick={createPlanningSession}
                    >
                        New Planning Session
                    </Button>
                }
            </Box>
            <QueryBuilder
                query={searchData.filters as any}
                search={searchData.setFilters as any}
                loading={searchData.loading}
                dataGridId={dataGridId}
                hideConditions
            />
            {
                !isMobile &&
                <PartnerDataGrid
                    id={dataGridId}
                    name={dataGridSaveConfig.name}
                    version={dataGridSaveConfig.version}
                    initialLayoutState={{columns: staticDataGridColumns}}
                    columns={savedDataGridColumns}
                    rows={dataGridRows}
                    hideFooter
                    getExportInfo={canExportPlanning ? getExportInfo : undefined}
                    state={{
                        sortBy: searchData.orderBy,
                        pagination: searchData.paginationInfo,
                        loading: {state: searchData.loading},
                        density: dataGridDensity,
                    }}
                />
            }
            {
                isMobile &&
                <DataCards<CardsDataEntry>
                    loading={searchData.loading}
                    loadMoreData={loadMoreData}
                    hasMoreToLoad={hasMoreToLoad}
                    data={cardsData}
                    cardTitle={getCardTitle}
                    cardTopActions={getCardTopActions}
                    cardProperties={cardProperties}
                />
            }
            <PurchasePlanningInformationUpdateNameDialog {...updateNameDialogState} />
        </>
    );
}

export default PurchasePlanningListView;
